import Vue from 'vue';
import VueRouter from 'vue-router';
import PageLayout from '@/views/layouts/PageLayout';
import Index from '@/views/pages/Index';
import Login from '@/views/pages/auth/Login';
import Passthrough from '@/components/General/Passthrough';
import store from '@/store/index';
import multiguard from 'vue-router-multiguard';

Vue.use(VueRouter);

/** Route Guards. These will allow passage to a route or otherwise reroute. */
const ifNotAuthenticated = function (to, from, next) {
    if (!store.getters['Auth/isAuthenticated']) {
        next();
    } else {
        next('/');
    }
};

const ifAuthenticated = function (to, from, next) {
    if (store.getters['Auth/isAuthenticated']) {
        if (!store.getters['User/isProfileLoaded']) {
            store.dispatch('User/userRequest').then(() => {
                next();
            });
        } else {
            next();
        }
    } else {
        Vue.swal.fire(
            'Login Required',
            'Please login again to verify your account',
            'warning'
        );
        next('/');
    }
};

const ifVerified = function (to, from, next) {
    if (
        store.getters['User/isProfileLoaded'] &&
        store.getters['User/isVerified']
    ) {
        next();
    } else {
        next('/verify-account');
    }
};

let routes = [
    {
        path: '/',
        component: PageLayout,
        children: [
            {
                path: '/',
                name: 'index',
                component: Index,
            },
            {
                path: '/download-app',
                name: 'download-app',
                component: () => import('@/views/pages/DownloadApp.vue'),
            },
            {
                path: '/about-us',
                name: 'about-us',
                component: () => import('@/views/pages/AboutUs'),
            },
            {
                path: '/how-it-works',
                name: 'how-it-works',
                component: () => import('@/views/pages/HowItWorks.vue'),
            },
            {
                path: '/support',
                name: 'support',
                component: () => import('@/views/pages/Support'),
            },
            {
                path: '/testimonials',
                name: 'testimonials',
                component: () => import('@/views/pages/Testimonials'),
            },
            {
                path: '/refund-policy',
                name: 'refund-policy',
                component: () => import('@/views/pages/RefundPolicy'),
            },
            {
                path: '/approvals/:action',
                props: true,
                name: 'approvals',
                component: () => import('@/views/pages/approval/Approval'),
            },
            {
                path: '/credit-payment',
                name: 'credit-payment',
                props: true,
                component: () =>
                    import('@/views/pages/messaging-app/CreditPayment'),
                beforeEnter: ifAuthenticated,
            },
            {
                path: '/',
                name: 'auth',
                component: Passthrough,
                children: [
                    {
                        path: '/login',
                        props: true,
                        name: 'auth.login',
                        component: Login,
                        beforeEnter: ifNotAuthenticated,
                    },
                    {
                        path: '/password/email',
                        name: 'auth.forgotten-password',
                        component: () =>
                            import('@/views/pages/auth/ForgottenPassword'),
                        beforeEnter: ifNotAuthenticated,
                    },
                    {
                        path: '/password/reset/:token',
                        name: 'password-reset',
                        component: () =>
                            import('@/views/pages/auth/PasswordReset'),
                    },
                ],
            },
            {
                path: '/verify-account',
                name: 'verify-account',
                component: () => import('@/views/pages/account/VerifyAccount'),
                beforeEnter: ifAuthenticated,
            },
        ],
    },

    // These don't use the standard header/footer section that most of the site uses.
    {
        path: '/visit-page/:id',
        name: 'visit-page',
        props: true,
        component: () => import('@/views/pages/visit-page/VisitPage'),
    },
    {
        path: '/ao-armor',
        component: Passthrough,
        children: [
            {
                path: 'messaging',
                name: 'ao-armor.messaging.index',
                props: true,
                component: () => import('@/views/pages/messaging-app/Index'),
                beforeEnter: multiguard([
                    ifAuthenticated,
                    ifVerified,
                ]),
            },
            {
                path: 'messaging/messages',
                name: 'ao-armor.messaging.messages',
                props: true,
                component: () => import('@/views/pages/messaging-app/Messages'),
                beforeEnter: multiguard([
                    ifAuthenticated,
                    ifVerified,
                ]),
            },
        ],
    },
];

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes,
    // eslint-disable-next-line no-unused-vars
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        } else {
            if (to.hash) {
                return {
                    selector: to.hash,
                };
            } else {
                return { x: 0, y: 0 };
            }
        }
    },
});

export default router;
